import { IVector3, VehicleType } from '../../types';
import { ConfigParser } from '../ConfigParser';

export class JobTaxiConfig {
	public readonly vehicleExitTimeout: number;
	public readonly vehicleSpawnPosition: IVector3;
	public readonly vehicleSpawnHeading: number;

	public readonly nextCustomerTimeoutRange: [number, number];
	public readonly customerPatienceTime: number;

	public readonly customerModels: string[];

	public readonly returnToJobVehicleTimeoutMs: number;

	public readonly maxDistanceFromCustomer: number;
	public readonly maxDistanceFromDropoff: number;

	public readonly minCustomerDistance: number;
	public readonly maxCustomerDistance: number;

	public readonly minDeliveryDistance: number;
	public readonly maxDeliveryDistance: number;

	public readonly allowedTypes: VehicleType[];

	public readonly markerSpeedLimit: number;

	public readonly rentVehicles: JobTaxiRentVehicleConfig[];
	public readonly pointPositions: JobTaxiPointPositionConfig[];

	constructor(tables: Record<string, any>) {
		const taxi = tables['common'];

		this.vehicleExitTimeout = ConfigParser.getInt(taxi, 'vehicle_exit_timeout');
		this.vehicleSpawnPosition = ConfigParser.getVector3(taxi, 'vehicle_spawn_position');
		this.vehicleSpawnHeading = ConfigParser.getFloat(taxi, 'vehicle_spawn_heading');

		this.nextCustomerTimeoutRange = ConfigParser.getIntArray(taxi, 'next_customer_timeout_range') as [
			number,
			number,
		];
		if (this.nextCustomerTimeoutRange.length !== 2) {
			throw new Error(
				`JobTaxiConfig: Invalid nextCustomerTimeoutRange value. Expected length 2 got ${this.nextCustomerTimeoutRange.length}`,
			);
		}

		this.customerModels = ConfigParser.getStringArray(taxi, 'customer_models');

		this.customerPatienceTime = ConfigParser.getInt(taxi, 'customer_patience_time');
		this.returnToJobVehicleTimeoutMs = ConfigParser.getInt(taxi, 'return_to_job_vehicle_timeout_ms');

		this.maxDistanceFromCustomer = ConfigParser.getFloat(taxi, 'max_distance_from_customer');
		this.maxDistanceFromDropoff = ConfigParser.getFloat(taxi, 'max_distance_from_dropoff');

		this.minCustomerDistance = ConfigParser.getFloat(taxi, 'min_customer_distance');
		this.maxCustomerDistance = ConfigParser.getFloat(taxi, 'max_customer_distance');

		this.minDeliveryDistance = ConfigParser.getFloat(taxi, 'min_delivery_distance');
		this.maxDeliveryDistance = ConfigParser.getFloat(taxi, 'max_delivery_distance');

		this.allowedTypes = ConfigParser.getEnumArray(taxi, 'allowed_types', VehicleType);

		const markerSpeedLimitKMH = ConfigParser.getInt(taxi, 'marker_speed_limit');
		this.markerSpeedLimit = Math.round(markerSpeedLimitKMH / 3.6);

		const taxiRentVehicles = tables['rent_vehicles'];
		this.rentVehicles = taxiRentVehicles.map((e: Record<string, unknown>) => new JobTaxiRentVehicleConfig(e));

		const customerPositionList = new Map<string, IVector3[]>();
		const taxiCustomerPositions = tables['customer_positions'];
		for (const row of taxiCustomerPositions) {
			const pointId = ConfigParser.getString(row, 'point_id');
			const customerPosition = ConfigParser.getVector3(row, 'customer_position');
			const customerPositions = customerPositionList.get(pointId);

			if (customerPositions) {
				customerPositions.push(customerPosition);
			} else {
				customerPositionList.set(pointId, [customerPosition]);
			}
		}

		this.pointPositions = [];
		const taxiPointPositions = tables['point_positions'];
		for (const point of taxiPointPositions) {
			const pointId = ConfigParser.getString(point, 'point_id');
			const customerPositions = customerPositionList.get(pointId) ?? [];

			const taxiPointConfig = new JobTaxiPointPositionConfig(point, customerPositions);
			this.pointPositions.push(taxiPointConfig);
		}
	}

	getClientConfig() {
		return {
			nextCustomerTimeoutRange: this.nextCustomerTimeoutRange,
			customerPatienceTime: this.customerPatienceTime,
			customerModels: this.customerModels,
			returnToJobVehicleTimeoutMs: this.returnToJobVehicleTimeoutMs,
			allowedTypes: this.allowedTypes,
			markerSpeedLimit: this.markerSpeedLimit,
			rentVehicles: this.rentVehicles,
		};
	}
}

export enum JobVehicleRentClass {
	Economy = 'economy',
	Comfort = 'comfort',
	Business = 'business',
	Luxury = 'luxury',
}

export enum JobVehicleRentCurrency {
	Ruble = 'ruble',
}

export class JobTaxiRentVehicleConfig {
	public readonly vehicleModel: string;
	public readonly vehicleClass: JobVehicleRentClass;
	public readonly minLevel: number;
	public readonly price: number;
	public readonly priceCurrency: JobVehicleRentCurrency;

	constructor(config: Record<string, any>) {
		this.vehicleModel = ConfigParser.getString(config, 'vehicle_model');
		this.vehicleClass = ConfigParser.getEnum(config, 'vehicle_class', JobVehicleRentClass);
		this.minLevel = ConfigParser.getInt(config, 'min_level');
		this.price = ConfigParser.getInt(config, 'price');
		this.priceCurrency = ConfigParser.getEnum(config, 'price_currency', JobVehicleRentCurrency);
	}
}

export class JobTaxiPointPositionConfig {
	public readonly position: IVector3;
	public readonly customerPositions: IVector3[];

	constructor(config: Record<string, any>, customerPositions: IVector3[]) {
		this.position = ConfigParser.getVector3(config, 'position');
		this.customerPositions = customerPositions;
	}
}
